import {
  ChangeHandler,
  DefaultValues,
  FieldValues,
  Path,
  useForm,
  UseFormRegister,
  UseFormRegisterReturn,
  UseFormReturn,
} from 'react-hook-form';
import { ContentType } from '../model/extras.domain';
import { toErrors } from '@client/new/pages/promotions/view-model/newDiscount.mappers';
import { useEffect, useRef, useState } from 'react';
import { Actions, Dispatchers } from '@client/new/helpers/types';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { extrasFormSchemaMap, validateImage } from './extras.validation';
import { extrasVM } from '../Extras.page';

export type ExtrasFormVMActionMap = {
  StartBannerUpload: null;
  RemoveBanner: null;
  UploadBanner: File;
  AddBanner: string;
  StartSlidesUpload: null;
  RemoveSlide: string;
  Submit: null;
};

type ExtrasFormVMActions = Actions<ExtrasFormVMActionMap>;

export type ExtrasFormVM<T extends FieldValues = FieldValues> = ReturnType<
  typeof useContentFormVM
> &
  Dispatchers<ExtrasFormVMActionMap> & {
    register: ExtendedFormRegister<T>;
  };

export type ExtendedFormRegisterReturn<T extends FieldValues> =
  UseFormRegisterReturn & {
    value: any;
    error?: string;
    onChange: ChangeHandler;
  };

export type ExtendedFormRegister<T extends FieldValues> = <K extends Path<T>>(
  name: K,
  options?: Parameters<UseFormRegister<T>>[1],
) => ExtendedFormRegisterReturn<T>;

export const useContentFormVM = <T extends FieldValues>(id: string) => {
  const extras = extrasVM.useValue();
  const content = { ...extras.contentMap[id] };
  const formType = content.type as ContentType;
  const defaultValues = content.value as unknown as DefaultValues<T>;
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const createdForm = useForm<T>({
    reValidateMode: 'onChange',
    defaultValues,
    resolver: zodResolver(extrasFormSchemaMap[formType]),
  });

  const [isUploading, setIsUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  let stored = extras.formMap[id] as UseFormReturn<T> | undefined;

  if (!stored) {
    extras.addForm(id, createdForm);
    stored = createdForm;
  }

  const form = stored;

  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues);
    }
  }, [id, JSON.stringify(defaultValues)]);

  const values = form.watch();

  const register: ExtendedFormRegister<T> = <K extends Path<T>>(
    name: K,
    options?: Parameters<UseFormRegister<T>>[1],
  ): ExtendedFormRegisterReturn<T> => {
    const res = form.register(name, options);
    const onChange: ChangeHandler = (e) => {
      form.setValue(name, e.target.value, { shouldValidate: true });
      form.trigger(name);
      return res.onChange(e);
    };

    return {
      ...res,
      value: form.watch(name),
      onChange,
      error: form.getFieldState(name).error?.message,
    };
  };

  const _dispatch = (a: ExtrasFormVMActions) => {
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      // TODO: Whole banner upload logic should be moved to a separate component separated from the viewmodel here;
      case 'UploadBanner': {
        if (isUploading) return;
        setIsUploading(true);

        // @ts-expect-error todo: fix this type
        form.clearErrors('coverImage');

        validateImage(a.payload).then((p) => {
          if (!p.isValid) {
            // @ts-expect-error todo: fix this type
            form.setError('coverImage', {
              type: 'manual',
              message: p.error || 'Invalid image format or size.',
            });
            // @ts-expect-error todo: fix this type
            form.setError('coverURL', {
              type: 'manual',
              message: p.error || 'Invalid image format or size.',
            });
            setIsUploading(false);
            return;
          }

          extras
            .upload(a.payload)
            .then(dispatchers.lazyDispatch('AddBanner'))
            .finally(() => setIsUploading(false));
        });

        return;
      }
      case 'AddBanner': {
        if (formType === 'ContentLink') {
          // @ts-expect-error todo: fix this type
          form.setValue('coverImage', a.payload, { shouldValidate: true });
        } else {
          // @ts-expect-error todo: fix this type by extracting cover image to component; remove this logic from viewmodel;
          form.setValue('coverURL', a.payload, { shouldValidate: true });
        }
        return;
      }
      case 'StartBannerUpload': {
        if (!uploadInputRef.current) return;
        uploadInputRef.current.click();
        break;
      }
      case 'RemoveBanner': {
        if (formType === 'ContentLink') {
          // @ts-expect-error todo: fix this type
          form.setValue('coverImage', '', { shouldValidate: false });
        } else {
          // @ts-expect-error todo: fix this type
          form.setValue('coverURL', '', { shouldValidate: false });
        }
        return;
      }
      case 'Submit': {
        form.trigger().then((isValid) => {
          if (isValid) {
            extras.dispatch('Submit');
          } else {
            setShowErrors(true);
          }
        });
      }
    }
  };

  const dispatchers = getDispatchers<ExtrasFormVMActionMap>(_dispatch);

  return {
    id,
    formType,
    register,
    reset: form.reset,
    errors: showErrors ? toErrors(form.formState.errors) : null,
    isUploading,
    uploadInputRef,
    ...dispatchers,
    values,
  };
};
